import { Component, inject, linkedSignal, output, signal } from '@angular/core';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { DatePipe } from '@angular/common';
import {
  AdviceMouseClickEvent,
  CropFieldAdvice,
} from '../../../../../shared/components/disease-overview-table.component/models/models';
import { DiseaseOverviewTableComponent } from '../../../../../shared/components/disease-overview-table.component/table.component';
import { RecalculateButtonComponent } from '../../../../../shared/components/recalculate-button/recalculate-button.component';
import { BlightAIIdentifiers } from '../../../../../blight-v2/shared/constants';
import { ActionMenuComponent } from '../../../../shared/components/action-menu.component';
import { BlightSignalStore } from '../../../../signal-store/blight-signal-store';
import { LegendComponent } from './components/legend.component';
import { CropYearService } from '../../../../../shared/services/crop-year.service';

@Component({
  selector: 'app-blight-advice-overview',
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
  imports: [
    RecalculateButtonComponent,
    ActionMenuComponent,
    DiseaseOverviewTableComponent,
    LegendComponent,
    DatePipe,
    TranslatePipe,
  ],
})
export class OverviewComponent {
  public readonly adviceClick = output<AdviceMouseClickEvent>();
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(BlightSignalStore);
  protected readonly lastCalculatedOn = linkedSignal<Date | null | undefined, Date | null>({
    source: () => (this.store.blightDataIsLoading() ? undefined : this.store.blightCalculationDate()),
    computation: (source, previous) => source ?? previous?.value ?? null,
  });
  protected readonly advices = linkedSignal<CropFieldAdvice[] | undefined, CropFieldAdvice[]>({
    source: () => (this.store.blightDataIsLoading() ? undefined : this.store.blightAdvices()),
    computation: (source, previous) => source ?? previous?.value ?? [],
  });
  protected readonly diseaseCropFields = linkedSignal<
    { id: number; name: string; areaInHectares: number; isVisible: boolean }[] | undefined,
    { id: number; name: string; areaInHectares: number; isVisible: boolean }[]
  >({
    source: () => (this.store.blightDataIsLoading() ? undefined : this.store.diseaseCropFields()),
    computation: (source, previous) => source ?? previous?.value ?? [],
  });
  protected readonly isRecalculationDisabled = linkedSignal<boolean | undefined, boolean>({
    source: () => {
      if (this.store.blightDataIsLoading()) return undefined;
      return (
        this.store.isBlightCropFieldsSyncing() ||
        this.advices().length <= 0 ||
        this.isCropYearReadOnly() ||
        !this.store.hasEligibleBlightCropFields()
      );
    },
    computation: (source, previous) => source ?? previous?.value ?? true,
  });

  protected readonly selectedAdvices = signal<CropFieldAdvice[]>([]);
  protected readonly localeService = inject(LocaleService);
  protected readonly locale = this.localeService.getLocale();
  private readonly ai = inject(AppInsightsServiceInjector);

  protected onCropFieldClick(event: AdviceMouseClickEvent): void {
    this.adviceClick.emit(event);
    this.store.selectCropField(event.cropField.cropFieldId);
  }

  protected onSelectedDatesChange(dates: Date[]): void {
    this.store.patchSelectedDates(dates);
  }

  protected onSelectedCropFieldsChange(selectedCropFields: CropFieldAdvice[]): void {
    this.selectedAdvices.set(selectedCropFields);
  }

  protected onCropFieldVisibilityChanged(event: { cropFieldId: number; isVisible: boolean }): void {
    const advice = this.advices().find((item) => item.cropFieldId === event.cropFieldId);
    if (!advice) {
      return;
    }

    this.store.updateCropFieldVisibility({
      cropFieldId: event.cropFieldId,
      year: advice.cropYear,
      isVisible: event.isVisible,
    });
  }

  protected onRecalculate(): void {
    if (this.isCropYearReadOnly()) {
      return;
    }

    this.ai.trackEvent(BlightAIIdentifiers.recalc, {});
    this.store.requestBlightRecalculation(this.store.eligibleBlightRecalculationRequests());
  }
}
